/**
 * Unified verification tracker strip — used on Steps 3, 4, and 5 of the
 * applicant flow and on the voucher connect-accounts step.
 *
 * Renders a thin, white card: uppercase "Verification tracker" eyebrow on
 * the left; 4 green pips + "N of 4 tracks verified" on the right.
 *
 * Two prop shapes are supported for backwards compatibility:
 *   • { filled } — caller passes the already-derived count.
 *   • { emailVerified, accountChecks } — legacy voucher-flow shape; filled
 *     is derived as (emailVerified ? 1 : 0) + min(accountChecks, 3).
 */
type Props =
  | { filled: number; total?: number; className?: string }
  | {
      emailVerified?: boolean;
      accountChecks: number;
      total?: number;
      className?: string;
    };

export function AccountChecksTracker(props: Props) {
  const total = props.total ?? 4;
  const filled =
    "filled" in props
      ? Math.max(0, Math.min(props.filled, total))
      : Math.min(
          (props.emailVerified === false ? 0 : 1) +
            Math.max(0, Math.min(props.accountChecks, total - 1)),
          total,
        );
  const className = props.className ?? "";

  return (
    <div
      className={`flex items-center justify-between gap-2.5 rounded-xl border border-border bg-card px-3 py-2.5 ${className}`}
      aria-label={`Verification tracker: ${filled} of ${total} tracks verified`}
    >
      <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-muted-foreground">
        Verification tracker
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <div className="flex items-center gap-1" aria-hidden="true">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-5 rounded-full ${i < filled ? "bg-trust-green" : "bg-border"}`}
            />
          ))}
        </div>
        <span className="text-[13px] text-foreground/80">
          {filled} of {total} tracks verified
        </span>
      </div>
    </div>
  );
}
